import { Fragment, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';

import { Button, ElasticFormModal } from 'basic-components';
import { DataLakeQuery } from 'dashboard-services/api/dataLake';
import Query from 'dashboard-services/api/Query';
import { CommonService } from 'dashboard-services/services';
import { CodeMirrorWrapper, ftlOptions, globalActions, statusBoxActions } from 'primary-components';

import { ftlEditorActions } from 'actions/modules/tools';
import getApiConfig from 'api/ApiConfig';
import { filesDatalakeQueries } from 'queries/modules/datalake';
import { sendEvent } from 'services/GAService';

import Iframe from 'basic/Iframe';

import { useQueryClient } from '@tanstack/react-query';
import { Else, If, Then, When } from 'react-if';

import './FtlEditor.scss';

const FtlEditor = () => {
  const dispatch = useDispatch(),
        [content, setContent] = useState(),
        [fileInfo, setFileInfo] = useState({}),
        [isPreviewActive, setIsPreviewActive] = useState(true),
        swapPreview = useCallback(() => setIsPreviewActive(p => !p), []),
        { fileId, disabled, provider } = useSelector(state => state.toolsState.ftlEditorState),
        doc = useRef(),
        getQueryBuilder = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
            .withHeader("Target", provider, true)
        , [dispatch, provider]),
        getQueryBuilderS3 = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
        , [dispatch]),
        { data: infoResponse } = filesDatalakeQueries.useGetFileDefinition({ getQueryBuilder, id: fileId, refetchOnMount: false, refetchOnReconnect: false, refetchOnWindowFocus: false }),
        location = useMemo(() => infoResponse?.headers?.get("Location"), [infoResponse]),
        { mutateAsync: s3GetCall, isPending } = filesDatalakeQueries.useGetFileContentMutation({ getQueryBuilder: getQueryBuilderS3, url: location }),
        downloadHtml = useCallback(() => {
          const reqId = CommonService.generateUID()
          dispatch(globalActions.handleRequest({ id: reqId }))
          return s3GetCall()
            .then(response => response.text())
            .then(response => setContent(response))
            .finally(() => dispatch(globalActions.handleRequest({ id: reqId, isResponse: true })))
        }, [dispatch, s3GetCall]),
        close = useCallback(() => {
          batch(() => {
            dispatch(ftlEditorActions.changeValue(undefined, "fileId"))
            dispatch(ftlEditorActions.changeValue(false, "open"))
            dispatch(ftlEditorActions.changeValue(false, "disabled"))
            dispatch(ftlEditorActions.changeValue(undefined, "provider"))
          })
        }, [dispatch]),
        queryClient = useQueryClient(),
        fileFields = useMemo(() => Object.entries(fileInfo?.fields || {}).map(([k, v]) => ({ name: k, value: v })), [fileInfo]),
        getQueryBuilderUpload = useCallback(() => 
          new Query.Builder(dispatch(getApiConfig()))
            .withOnParse(response => Promise.resolve(response))
        , [dispatch]),
        invalidateRef = useRef(),
        { uploadFileMetadata, uploadFileContent } = filesDatalakeQueries.useFileUpload({ getQueryBuilder: getQueryBuilderUpload, fileName: fileInfo?.fileName, groupName: fileInfo?.groupName, fileType: fileInfo?.fileType, fields: fileFields }),
        onSubmit = useCallback(() => {
          if(isPending) {
            return Promise.resolve();
          }
          if(disabled) {
            return Promise.resolve();
          }
          const reqId = CommonService.generateUID()
          dispatch(globalActions.handleRequest({ id: reqId }))
          return uploadFileMetadata()
            .then(response => {
              const jobId = response.headers.get("X-KH-JOB-ID")
              dispatch(statusBoxActions.addJob({ jobId, displayName: fileInfo.fileName, subtitle: fileInfo.groupName }))
              return response
            })
            .then(response => response.json())
            .then(response => uploadFileContent({
              url: response.location,
              image: content
            }))
            .then(() => dispatch(globalActions.getMessageStore()).success("New version of file has been saved."))
            .then(() => {
              close()
              clearTimeout(invalidateRef.current)
              invalidateRef.current = setTimeout(() => queryClient.invalidateQueries({ queryKey: DataLakeQuery.KEYS.SEARCH_FILES() }), 3000)
            })
            .finally(() => dispatch(globalActions.handleRequest({ id: reqId, isResponse: true })))
        }, [isPending, disabled, dispatch, uploadFileMetadata, fileInfo.fileName, fileInfo.groupName, uploadFileContent, content, close, queryClient]),
        submitProps = useMemo(() => ({
          disabled
        }), [disabled]),
        buttons = useCallback(() => (
          <Fragment key="fragment">
            <Button
                onClick={swapPreview}
                size="small"
                type="button"
                variant="secondary"
            >
              {isPreviewActive ? "Show code" : "Preview"}
            </Button>
          </Fragment>
        ), [isPreviewActive, swapPreview])

  useEffect(() => {
    if(location) {
      downloadHtml()
    }
  }, [downloadHtml, location])

  useEffect(() => {
    const event = {
      category: "view_item",
      action: "ftl_editor",
      label: "tool",
    };
    dispatch(sendEvent(event))
  }, [dispatch])

  useEffect(() => {
    if(!infoResponse?.bodyUsed) {
      infoResponse?.json().then(r => setFileInfo(r || {}))
    }
  }, [infoResponse])

  useEffect(() => () => {
    clearTimeout(invalidateRef.current)
  }, [])

  return (
    <Fragment>
      <When condition={content !== undefined}>
        {() => (
          <ElasticFormModal
              buttons={buttons}
              className="ng-ftl-editor"
              closeOnBackground={false}
              onClose={close}
              onSubmit={onSubmit}
              submitProps={submitProps}
              title="Ftl Editor"
          >
            <If condition={isPreviewActive}>
              <Then>
                {() => (
                  <Iframe
                      content={content}
                  />
                )}
              </Then>
              <Else>
                {() => (
                  <CodeMirrorWrapper
                      onChange={setContent}
                      options={ftlOptions}
                      ref={doc}
                      value={content}
                  />
                )}
              </Else>
            </If>
          </ElasticFormModal>
        )}
      </When>
    </Fragment>
  )
}

export default memo(FtlEditor);